import { FC, useCallback, useMemo } from 'react'
import { PatientIdentityBlockProps } from './PatientIdentityBlock.model'
import { Sex, Title } from '../../../../model/Patient'
import {
  FormBlock,
  FieldWrapper,
  RadioGroup,
  Radio,
  FormikTextInput,
  FormikDateInput,
  FormikSelectInput,
} from '../../../shared'
import { DisabledINSITooltipWrapper } from '../DisabledINSITootipWrapper'
import { PatientBirthPlaceCodeBlock } from '../PatientBirthPlaceCodeBlock'
import {
  computeFirstNames,
  findFirstNameOption,
  mapSexToDefaultTitle,
  toLocaleUpperCase,
} from '../PatientForm.utils'
import { PatientFormFields, SEX_FORM_LABELS, TITLE_FORM_LABELS } from '../PatientForm.model'
import { useFormikContext } from 'formik'
import { useDisableInsiFields } from '../useDisableInsiFields.hook'
import { useResetINSIStatusHook } from '../useResetINSIStatus.hook'
import { isINSIValidated } from '../../../../misc/patient.utilities'
import styles from './PatientIdentityBlock.module.scss'

export const PatientIdentityBlock: FC<PatientIdentityBlockProps> = ({
  patient,
  isIdentityVigilanceEnabled,
}) => {
  const formikProps = useFormikContext<PatientFormFields>()
  const disableInsiFields = useDisableInsiFields() && !isIdentityVigilanceEnabled
  const resetINSIStatus = useResetINSIStatusHook()

  const firstNamesOptions = useMemo(() => {
    if (!formikProps.values.otherFirstNames || formikProps.values.otherFirstNames.trim() === '') {
      return []
    }
    const namesValue = formikProps.values.otherFirstNames
    const rawFirstNamesOption = computeFirstNames(namesValue)
    return rawFirstNamesOption.map((value: string) => ({
      label: value,
      name: value,
      value: value,
    }))
  }, [formikProps.values.otherFirstNames])

  const findSelectedFirstName = useCallback(
    (value?: string) => findFirstNameOption(firstNamesOptions, value) ?? undefined,
    [firstNamesOptions],
  )

  return (
    <FormBlock icon="profile" label="Patient">
      <FieldWrapper>
        <DisabledINSITooltipWrapper display={isINSIValidated(formikProps.values.identityStatus)}>
          <FieldWrapper errorMessage={formikProps.submitCount === 0 ? '' : formikProps.errors.sex}>
            <RadioGroup
              name="sex"
              label="Sexe *"
              disabled={disableInsiFields}
              onChange={(event) => {
                formikProps.setValues(
                  {
                    ...formikProps.values,
                    sex: Sex[event.target.value],
                    title: formikProps.getFieldMeta('title').touched
                      ? formikProps.values.title
                      : mapSexToDefaultTitle[event.target.value],
                  },
                  true,
                )
                resetINSIStatus()
              }}
              value={formikProps.values.sex}
              testId="radio_group-patient_sex"
            >
              <Radio
                label={SEX_FORM_LABELS[Sex.MALE]}
                value={Sex.MALE}
                testId="radio-patient_sex-male"
              />
              <Radio
                label={SEX_FORM_LABELS[Sex.FEMALE]}
                value={Sex.FEMALE}
                testId="radio-patient_sex-female"
              />
              <Radio
                label={SEX_FORM_LABELS[Sex.UNKNOWN]}
                value={Sex.UNKNOWN}
                testId="radio-patient_sex-unknown"
              />
            </RadioGroup>
          </FieldWrapper>
        </DisabledINSITooltipWrapper>
        {!isIdentityVigilanceEnabled && (
          <RadioGroup
            name="title"
            label="Civilité"
            onChange={(event) => {
              formikProps.handleChange(event)
              formikProps.setFieldTouched('title', true, false)
            }}
            value={formikProps.values.title}
            testId="radio_group-patient_title"
          >
            <Radio
              label={TITLE_FORM_LABELS[Title.MR]}
              value={Title.MR}
              testId="radio-patient_title-mister"
            />
            <Radio
              label={TITLE_FORM_LABELS[Title.MS]}
              value={Title.MS}
              testId="radio-patient_title-madam"
            />
            <Radio
              label={TITLE_FORM_LABELS[Title.NONE]}
              value={Title.NONE}
              testId="radio-patient_title-none"
            />
          </RadioGroup>
        )}
      </FieldWrapper>
      <div className={styles.patientIdentityBlockNames}>
        <DisabledINSITooltipWrapper display={isINSIValidated(formikProps.values.identityStatus)}>
          <FormikTextInput
            fieldName="birthLastName"
            label="Nom de naissance"
            autocomplete={false}
            colorPreset="light"
            transform={toLocaleUpperCase}
            disabled={disableInsiFields}
            onChange={resetINSIStatus}
            testId="input-patient-birth_lastname-enter"
            required
          />
        </DisabledINSITooltipWrapper>
        {firstNamesOptions?.length === 0 ? (
          <FormikTextInput
            fieldName="birthFirstName"
            label="Premier prénom de naissance"
            autocomplete={false}
            colorPreset="light"
            required
            onChange={resetINSIStatus}
            testId="input-patient-birth_firstname-enter"
          />
        ) : (
          <FormikSelectInput
            label="Premier prénom de naissance"
            fieldName="birthFirstName"
            placeholder="Veuillez renseigner un prenom"
            options={firstNamesOptions}
            value={findSelectedFirstName(formikProps.values.birthFirstName)}
            onChange={resetINSIStatus}
            testId="patient-health_informations-assigning_authority_oid"
          />
        )}
      </div>
      <div className={styles.patientIdentityBlockNames}>
        <DisabledINSITooltipWrapper display={isINSIValidated(formikProps.values.identityStatus)}>
          <FormikTextInput
            fieldName="otherFirstNames"
            label="Prénoms de naissance"
            autocomplete={false}
            colorPreset="light"
            disabled={disableInsiFields}
            onChange={resetINSIStatus}
            testId="input-patient-birth_other_firstnames-enter"
          />
        </DisabledINSITooltipWrapper>
        <DisabledINSITooltipWrapper display={isINSIValidated(formikProps.values.identityStatus)}>
          <FormikDateInput
            fieldName="birthDate"
            label="Date de naissance"
            placeholder="jj/mm/aaaa"
            autocomplete={false}
            disabled={disableInsiFields}
            colorPreset="light"
            required
            onChange={resetINSIStatus}
            testId="date_input-patient-birth_date"
          />
        </DisabledINSITooltipWrapper>
      </div>
      {!isIdentityVigilanceEnabled && (
        <div className={styles.patientIdentityBlockNames}>
          <FormikTextInput
            fieldName="usedLastName"
            label="Nom utilisé"
            autocomplete={false}
            colorPreset="light"
            testId="input-patient-used_lastname-enter"
          />
          <FormikTextInput
            fieldName="usedFirstName"
            label="Prénom utilisé"
            autocomplete={false}
            colorPreset="light"
            testId="input-patient-used_firstname-enter"
          />
        </div>
      )}
      {!isIdentityVigilanceEnabled && (
        <FormikTextInput
          fieldName="profession"
          label="Profession"
          colorPreset="light"
          testId="input-patient-profession-enter"
        />
      )}
      <PatientBirthPlaceCodeBlock patient={patient} disabled={disableInsiFields} />
    </FormBlock>
  )
}
